import { ChangeDetectionStrategy, Component, computed, input, type InputSignal, type Signal } from '@angular/core';
import type { WishlistCreateBeneficiaryOption, WishlistCreateDraft } from '@nexus/customer/wishlists/model';
import { WISHLIST_CREATE_OCCASION_LABELS } from '@nexus/customer/wishlists/util';
import { formatWishlistGiftCount } from '../../utils/format-wishlist-gift-count';

interface SummaryRow {
  readonly label: string;
  readonly value: string;
}

@Component({
  selector: 'wishlist-create-summary',
  imports: [],
  templateUrl: './wishlist-create-summary.component.html',
  styleUrl: './wishlist-create-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WishlistCreateSummaryComponent {
  public readonly draft: InputSignal<WishlistCreateDraft> = input.required<WishlistCreateDraft>();
  public readonly beneficiary: InputSignal<WishlistCreateBeneficiaryOption | null> =
    input<WishlistCreateBeneficiaryOption | null>(null);
  protected readonly giftCountLabel: Signal<string> = computed(() => formatWishlistGiftCount(this.draft().items.length));

  protected readonly rows: Signal<readonly SummaryRow[]> = computed((): readonly SummaryRow[] => {
    const draft: WishlistCreateDraft = this.draft();
    const beneficiary: WishlistCreateBeneficiaryOption | null = this.beneficiary();
    const rows: SummaryRow[] = [
      {
        label: 'Nazwa',
        value: draft.title.trim().length > 0 ? draft.title.trim() : 'Bez nazwy',
      },
      {
        label: 'Dla kogo',
        value: beneficiary?.displayName ?? 'Nie wybrano',
      },
    ];
    const description: string = draft.description.trim();

    if (description.length > 0) {
      rows.push({
        label: 'Opis',
        value: description,
      });
    }

    rows.push(
      {
        label: 'Okazja',
        value: this.toOccasionCopy(draft),
      },
      {
        label: 'Widoczność',
        value: this.toVisibilityCopy(draft),
      },
      {
        label: 'Prezenty',
        value: `${draft.items.length}`,
      },
    );

    return rows;
  });

  private toOccasionCopy(draft: WishlistCreateDraft): string {
    if (draft.occasion === null) {
      return 'Bez okazji';
    }

    const label: string =
      draft.occasion === 'custom'
        ? draft.customOccasionLabel.trim() || WISHLIST_CREATE_OCCASION_LABELS.custom
        : WISHLIST_CREATE_OCCASION_LABELS[draft.occasion];

    return draft.occasionDate.trim().length > 0 ? `${label}, ${draft.occasionDate}` : label;
  }

  private toVisibilityCopy(draft: WishlistCreateDraft): string {
    if (draft.visibility === 'household') {
      return 'Domownicy';
    }

    if (draft.visibility === 'invited_only') {
      return 'Zaproszone osoby';
    }

    if (draft.visibility === 'anyone_with_link') {
      return 'Każdy z linkiem';
    }

    return 'Tylko ja';
  }
}
